import React, { useCallback, useEffect, useRef, useState } from "react";
import { paymentService, QR_EXPIRE_SECONDS, QR_POLL_MS, QR_INQUIRY_MS, ORDER_TYPE } from "../services";
import { useI18n } from "../i18n";
import { Button, Modal, Skeleton, baht } from "./ui";
import Icon from "./ui/Icon";

const SUCCESS_STATUS = ["success", "settled", "completed"];
const FAILED_STATUS = ["failed", "expired", "cancelled", "reject", "rejected"];

const clock = (seconds) => {
  const safe = Math.max(0, seconds);
  return `${String(Math.floor(safe / 60)).padStart(2, "0")}:${String(safe % 60).padStart(2, "0")}`;
};

/**
 * โมดัลชำระเงินด้วย QR — ใช้ร่วมกันทุก flow ของระบบ
 * (ใบแจ้งหนี้ / มัดจำการจอง / ตะกร้า / POS / payment link / เติมเงิน / ค่างวด)
 * ยิง API จริงที่ transactions/deposits/partner แล้ว poll สถานะจนกว่าจะจ่ายสำเร็จ
 */
export default function PaymentQrModal({ open, title, subtitle, amount, items, orderType = ORDER_TYPE.invoice, reference, orgId, onCreated, onClose, onPaid }) {
  const { tx } = useI18n();
  const [qr, setQr] = useState(null);
  const [status, setStatus] = useState("creating");
  const [error, setError] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(QR_EXPIRE_SECONDS);
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const expiredAtRef = useRef(null);
  const paidRef = useRef(false);

  // สร้าง QR ตอนเปิดโมดัล
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    paidRef.current = false;
    setQr(null);
    setError("");
    setStatus("creating");
    setSecondsLeft(QR_EXPIRE_SECONDS);

    paymentService
      .createQrOrder({ amount, items, remark: title, orderType, reference, orgId })
      .then((result) => {
        if (!alive) return;
        setQr(result);
        setStatus("pending");
        // บอกหน้าที่เรียกว่ามี QR/ธุรกรรมแล้ว (เช่น หน้า /pay จำไว้ให้ร้านกดตรวจสอบสถานะ) — พังไม่กระทบการจ่าย
        Promise.resolve(onCreated?.(result)).catch(() => {});
        if (result.expireDatetime) expiredAtRef.current = new Date(result.expireDatetime).getTime();
      })
      .catch((err) => {
        if (!alive) return;
        setError(err.message);
        setStatus("error");
      });

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const refresh = useCallback(async () => {
    if (!qr?.transactionId) return;
    const data = await paymentService.getTransaction(qr.transactionId);
    if (!data) return;
    if (data.expiredAt) expiredAtRef.current = new Date(data.expiredAt).getTime();
    setStatus(data.status);
  }, [qr]);

  // poll สถานะ + ยิง inquiry เป็นระยะ
  useEffect(() => {
    if (!open || !qr?.transactionId) return undefined;
    if (SUCCESS_STATUS.includes(status) || FAILED_STATUS.includes(status)) return undefined;

    const pollTimer = setInterval(() => refresh().catch(() => {}), QR_POLL_MS);
    const inquiryTimer = setInterval(() => paymentService.inquiry(qr.transactionId).catch(() => {}), QR_INQUIRY_MS);
    return () => {
      clearInterval(pollTimer);
      clearInterval(inquiryTimer);
    };
  }, [open, qr, status, refresh]);

  // นับถอยหลัง
  useEffect(() => {
    if (!open) return undefined;
    const timer = setInterval(() => {
      if (expiredAtRef.current) setSecondsLeft(Math.round((expiredAtRef.current - Date.now()) / 1000));
      else setSecondsLeft((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [open]);

  // แจ้งผลเมื่อจ่ายสำเร็จ — เงินถูกตัดจริงแล้ว ณ จุดนี้ ถ้าบันทึกออเดอร์/นัดหมายฝั่งเราไม่สำเร็จ
  // ห้ามเงียบ ต้องโชว์ให้เห็นชัด พร้อมปุ่มลองบันทึกซ้ำ (ไม่สร้าง QR/เรียกเก็บเงินซ้ำ)
  const runSavePaid = useCallback(async () => {
    if (!qr) return;
    setSaving(true);
    setSaveError("");
    try {
      await onPaid?.({ ...qr, status });
    } catch (err) {
      setSaveError(err?.message || tx("บันทึกข้อมูลไม่สำเร็จ", "Failed to save"));
    } finally {
      setSaving(false);
    }
  }, [onPaid, qr, status, tx]);

  useEffect(() => {
    if (!SUCCESS_STATUS.includes(status) || paidRef.current) return;
    paidRef.current = true;
    runSavePaid();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const download = () => {
    if (!qr?.base64) return;
    const link = document.createElement("a");
    link.href = qr.base64;
    link.download = `qr_${qr.refNo || qr.transactionId}.png`;
    link.click();
  };

  const isPaid = SUCCESS_STATUS.includes(status);
  const isFailed = FAILED_STATUS.includes(status) || secondsLeft <= 0;

  return (
    <Modal
      open={open}
      title={tx("ชำระเงินด้วย QR", "Pay with QR")}
      size="sm"
      onClose={onClose}
      footer={
        <>
          <Button onClick={download} disabled={!qr?.base64}>
            {tx("ดาวน์โหลด QR", "Download QR")}
          </Button>
          <Button variant="primary" onClick={onClose}>
            {tx("ปิด", "Close")}
          </Button>
        </>
      }
    >
      <div className="u-center">
        <div className="u-strong u-fs-18">{title}</div>
        {subtitle ? <div className="u-fs-13 u-muted">{subtitle}</div> : null}
        <div style={{ fontSize: 28, fontWeight: 900, margin: "10px 0" }}>{baht(amount)}</div>

        {status === "creating" ? <Skeleton height={260} /> : null}

        {error ? (
          <div className="card-crm" style={{ borderColor: "var(--c-rose)", background: "#fff1f2" }}>
            <div className="u-strong u-danger u-mb-8">{tx("สร้าง QR ไม่สำเร็จ", "Cannot create QR")}</div>
            <div className="u-fs-13">{error}</div>
          </div>
        ) : null}

        {qr?.base64 ? (
          <div className="qr-box" style={{ opacity: isFailed && !isPaid ? 0.3 : 1 }}>
            <img src={qr.base64} alt="QR" />
          </div>
        ) : null}

        {isPaid && saveError ? (
          <div className="card-crm u-mt-16" style={{ background: "#fff7ed", borderColor: "#fdba74" }}>
            <div style={{ fontSize: 34 }}><Icon name="warning" /></div>
            <div className="u-strong">{tx("ชำระเงินสำเร็จแล้ว แต่บันทึกรายการไม่สำเร็จ", "Payment received, but saving failed")}</div>
            <div className="u-fs-13 u-mt-8">{tx("ระบบจะไม่เรียกเก็บเงินซ้ำ กรุณากดลองบันทึกอีกครั้ง หรือแจ้งเจ้าหน้าที่พร้อมเลข Ref ด้านล่าง", "The system will not charge again. Please retry saving, or contact staff with the Ref number below.")}</div>
            <div className="u-fs-12 u-danger u-mt-8">{saveError}</div>
            <Button className="u-mt-8" onClick={runSavePaid} disabled={saving}>
              {saving ? tx("กำลังบันทึก...", "Saving...") : tx("ลองบันทึกอีกครั้ง", "Retry saving")}
            </Button>
          </div>
        ) : isPaid ? (
          <div className="card-crm u-mt-16" style={{ background: "#ecfdf5", borderColor: "#6ee7b7" }}>
            <div style={{ fontSize: 34 }}><Icon name="success" /></div>
            <div className="u-strong">{tx("ชำระเงินสำเร็จแล้ว", "Payment received")}</div>
          </div>
        ) : isFailed ? (
          <div className="u-danger u-mt-16 u-fs-13">{tx("QR หมดอายุหรือรายการถูกยกเลิก", "QR expired or cancelled")}</div>
        ) : qr ? (
          <div className="u-mt-16" style={{ background: "var(--c-indigo-50)", borderRadius: 10, padding: 12 }}>
            <div className="u-fs-12 u-muted">{tx("QR จะหมดอายุในอีก", "Expires in")}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: "var(--c-indigo-600)" }}>{clock(secondsLeft)}</div>
            <div className="u-fs-12 u-muted">{tx("ระบบกำลังตรวจสอบการชำระเงินอัตโนมัติ...", "Checking payment automatically...")}</div>
          </div>
        ) : null}

        {qr?.refNo ? <div className="u-fs-12 u-muted u-mt-8">Ref: {qr.refNo}</div> : null}
      </div>
    </Modal>
  );
}
